import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { MessageSquarePlus, X, ExternalLink, CheckCircle2, Download } from 'lucide-react'
import html2canvas from 'html2canvas'

const APP_VERSION = '0.1.0'
const GITHUB_REPO = 'Veytra-Technologies/Telematics-Backend'

function getOS(): string {
  const ua = navigator.userAgent
  if (/Windows NT 1[01]/.test(ua)) return 'Windows 10/11'
  if (/Windows/.test(ua))          return 'Windows'
  if (/Mac OS X/.test(ua))         return 'macOS'
  if (/Android/.test(ua))          return 'Android'
  if (/iPhone|iPad/.test(ua))      return 'iOS'
  if (/Linux/.test(ua))            return 'Linux'
  return 'Unknown'
}

interface Props {
  open: boolean
  onClose: () => void
  screenshot: string | null
  onClearScreenshot: () => void
}

export function FeedbackPanel({ open, onClose, screenshot, onClearScreenshot }: Props) {
  const location = useLocation()
  const [title, setTitle] = useState('')
  const [body, setBody]   = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [githubUrl, setGithubUrl] = useState('')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const bodyLines = [
      body.trim(),
      '',
      '---',
      `**Page:** \`${location.pathname}\``,
      `**OS:** ${getOS()}`,
      `**App:** v${APP_VERSION} · web`,
      screenshot
        ? '_📎 Screenshot captured — download it from the app and attach to this issue._'
        : '',
    ].filter(s => s !== undefined)

    const url = new URL(`https://github.com/${GITHUB_REPO}/issues/new`)
    url.searchParams.set('title', `[Frontend] ${title}`)
    url.searchParams.set('body', bodyLines.join('\n'))
    url.searchParams.set('labels', 'bug,frontend-reported')

    const ghUrl = url.toString()
    window.open(ghUrl, '_blank', 'noopener,noreferrer')
    setGithubUrl(ghUrl)
    setSubmitted(true)
  }

  const downloadScreenshot = () => {
    if (!screenshot) return
    const a = document.createElement('a')
    a.href = screenshot
    a.download = `screenshot-${Date.now()}.png`
    a.click()
  }

  const handleClose = () => {
    setTitle(''); setBody(''); setSubmitted(false); setGithubUrl('')
    onClose()
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl border border-border bg-surface shadow-2xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
          <div className="flex items-center gap-2">
            <MessageSquarePlus className="h-4 w-4 text-primary" />
            <p className="text-sm font-semibold text-muted-fg">Report an Issue</p>
          </div>
          <button onClick={handleClose} className="rounded p-1 text-muted hover:bg-surface-overlay hover:text-muted-fg">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 px-4 py-4">
          {submitted ? (
            /* Success state */
            <div className="space-y-4 text-center py-4">
              <CheckCircle2 className="h-12 w-12 text-success mx-auto" />
              <div>
                <p className="text-sm font-semibold text-muted-fg">Issue form opened in GitHub</p>
                <p className="text-xs text-muted mt-1">Review the pre-filled details and click <strong>Submit new issue</strong>.</p>
              </div>
              {screenshot && (
                <button
                  onClick={downloadScreenshot}
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary/10 border border-primary/20 px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/20"
                >
                  <Download className="h-3.5 w-3.5" /> Download Screenshot to attach
                </button>
              )}
              <a
                href={githubUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-surface-overlay"
              >
                Reopen GitHub tab <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          ) : (
            /* Form */
            <form id="feedback-form" onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-muted mb-1">Title *</label>
                <input
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="Brief summary of the issue…"
                  required
                  className="h-8 w-full rounded-md border border-border bg-surface-overlay px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted mb-1">Description</label>
                <textarea
                  value={body}
                  onChange={e => setBody(e.target.value)}
                  placeholder="Steps to reproduce, expected vs actual behaviour…"
                  rows={4}
                  className="w-full rounded-md border border-border bg-surface-overlay px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>

              {/* Screenshot */}
              <div>
                <label className="block text-xs font-medium text-muted mb-1">Screenshot</label>
                {screenshot ? (
                  <div className="relative rounded-lg overflow-hidden border border-border">
                    <img src={screenshot} alt="Screenshot" className="w-full h-32 object-cover object-top" />
                    <button
                      type="button"
                      onClick={onClearScreenshot}
                      className="absolute top-1.5 right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
                    >
                      <X className="h-3 w-3" />
                    </button>
                    <div className="absolute bottom-0 inset-x-0 bg-black/50 px-2 py-1">
                      <p className="text-[10px] text-white">Screenshot captured — you can download &amp; attach it on GitHub</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-muted italic">No screenshot captured. Close this modal and reopen to auto-capture.</p>
                )}
              </div>

              {/* Meta */}
              <div className="rounded-lg bg-surface-overlay/60 border border-border px-3 py-2 space-y-0.5">
                <p className="text-[10px] text-muted">Page: <span className="text-muted-fg">{location.pathname}</span></p>
                <p className="text-[10px] text-muted">OS: <span className="text-muted-fg">{getOS()}</span></p>
                <p className="text-[10px] text-muted">App: <span className="text-muted-fg">v{APP_VERSION} · web</span></p>
              </div>

              <p className="text-[10px] text-muted">
                This will open <strong>GitHub Issues</strong> in a new tab with your report pre-filled.
              </p>
            </form>
          )}
        </div>

        {/* Footer */}
        {!submitted && (
          <div className="shrink-0 flex justify-end gap-2 px-4 py-3 border-t border-border">
            <button type="button" onClick={handleClose}
              className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-surface-overlay">
              Cancel
            </button>
            <button type="submit" form="feedback-form" disabled={!title.trim()}
              className="flex items-center gap-1.5 rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50">
              <ExternalLink className="h-3.5 w-3.5" />
              Open on GitHub
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

/** Floating trigger button — renders the panel and handles screenshot capture */
export function FeedbackButton() {
  const [open, setOpen]             = useState(false)
  const [screenshot, setScreenshot] = useState<string | null>(null)
  const capturing                   = { current: false }

  const openPanel = async () => {
    if (capturing.current) return
    capturing.current = true
    try {
      const canvas = await html2canvas(document.body, {
        useCORS: true, allowTaint: true, scale: 0.5, logging: false,
      })
      setScreenshot(canvas.toDataURL('image/png'))
    } catch {
      setScreenshot(null)
    } finally {
      capturing.current = false
    }
    setOpen(true)
  }

  return (
    <>
      <button
        onClick={openPanel}
        title="Report an issue / Give feedback"
        className="fixed bottom-5 right-5 z-40 flex h-11 w-11 items-center justify-center rounded-full bg-primary shadow-lg text-white hover:bg-primary/90 transition-transform hover:scale-105"
      >
        <MessageSquarePlus className="h-5 w-5" />
      </button>

      <FeedbackPanel
        open={open}
        onClose={() => setOpen(false)}
        screenshot={screenshot}
        onClearScreenshot={() => setScreenshot(null)}
      />
    </>
  )
}
